import React, { useState, useEffect, useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import type { BiddingInfo } from '../../../types';
import type { ListingCategoryType } from '../../../constants';
import { ListingCategoryConst } from '../../../constants';
import { NumericKeypad } from '../../../components/common/NumericKeypad';
// 入力用Props
interface BiddingInputFormProps {
  item: BiddingInfo;
  onSubmit: (bidData: { itemId: string, branchNumber: number, bidQuantity1: number, bidQuantity2: number, bidPrice: number }) => void;
  isSaving?: boolean;
  listingCategory: ListingCategoryType;
  isBidDisabled?: boolean;
}

// 入力用コンポーネント
export const BiddingInputForm: React.FC<BiddingInputFormProps> = ({ item, onSubmit, isSaving = false, listingCategory, isBidDisabled = false }) => {
  //----------------
  // State定義
  //----------------
  const [bidPrice, setBidPrice] = useState<number | ''>(item.bidPrice || '');
  const [bidQuantity1, setBidQuantity1] = useState<number | ''>(item.bidQuantity1 || '');
  const [bidQuantity2, setBidQuantity2] = useState<number | ''>(item.bidQuantity2 || '');

  // キーパッドを表示するかどうかのフラグ
  const [showKeypad, setShowKeypad] = useState(false);

  // スマホ判定
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // --- キーパッド用ロジック  ---
  const handleNumClick = (num: string) => {
    const currentStr = bidPrice === '' ? '' : String(bidPrice);
    if (currentStr.length >= 7) return; 
    setBidPrice(Number(currentStr + num));
  };

  const handleDelete = () => {
    if (bidPrice === '') return;
    const str = String(bidPrice);
    if (str.length <= 1) {
      setBidPrice('');
    } else {
      setBidPrice(Number(str.slice(0, -1)));
    }
  };

  const handleClear = () => {
    setBidPrice('');
  };

  const handlePriceFocus = () => {
    if (isMobile) {
      console.log(showKeypad)　//ビルドエラー回避用
      setShowKeypad(true);
    }
  };

  //----------------
  // Effect定義
  //----------------
  // 親から渡されるitem情報が変わったら（例：再フェッチ後）、フォームも更新
  useEffect(() => {
    setBidPrice(item.bidPrice || '');
    setBidQuantity1(item.bidQuantity1 || '');
    setBidQuantity2(item.bidQuantity2 || '');
  }, [item.bidPrice, item.bidQuantity1, item.bidQuantity2]);

  //----------------
  // イベントハンドラー定義
  //----------------
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault(); 

    const isRegularBidding = listingCategory === ListingCategoryConst.BIDDING; // 通常入札

    onSubmit({
      itemId: item.itemId,
      branchNumber: item.branchNumber,
      bidPrice: Number(bidPrice) || 0,
      // 通常入札なら全量、そうでなければフォームの値
      bidQuantity1: isRegularBidding ? Number(item.quantity1) : (Number(bidQuantity1) || 0), 
      bidQuantity2: isRegularBidding ? Number(item.quantity2) : (Number(bidQuantity2) || 0),
    });
  }, [item, bidPrice, bidQuantity1, bidQuantity2]);

  //----------------
  // その他定数定義
  //----------------
  const inputClasses = "mt-1 w-full p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500";
  const labelClasses = "text-sm font-medium text-slate-700";

  //----------------
  // レンダリング処理
  //----------------
  return (
    <div className="p-2 lg:p-4 bg-slate-50 h-full flex flex-col">
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* 入札会(SALE)の場合のみ入札数①②を表示 */}
        {listingCategory === ListingCategoryConst.SALE && (        
        <>
          {/* --- 入札数① --- */}
          <div className="form-group">
            <label className={labelClasses}>入札数① (出品: {item.capacity1}kg × {item.quantity1}本)</label>
            <input type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="bidQuantity1" 
              value={bidQuantity1 === 0 ? '' : bidQuantity1}
              onChange={(e) => setBidQuantity1(e.target.value === '' ? '' : Number(e.target.value))}
              className={inputClasses}
              placeholder="0"
              max={item.quantity1} // 最大値を設定
              min={0}
            />
          </div>
          {/* --- 入札数② --- */}
          <div className="form-group">
            <label className={labelClasses}>入札数② (出品: {item.capacity2}kg × {item.quantity2}本)</label>
            <input type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="bidQuantity2" 
              value={bidQuantity2 === 0 ? '' : bidQuantity2}
              onChange={(e) => setBidQuantity2(e.target.value === '' ? '' : Number(e.target.value))}
              className={inputClasses}
              placeholder="0"
              max={item.quantity2} // 最大値を設定
              min={0}
            />
          </div>
        </>
        )}
        
        {/* --- 入札額 (kg単価) --- */}
        <div className="form-group md:col-span-1">
          {/* 【スマホ表示】入力欄とボタンを一体化してバランスを整える */}
          <div className="md:hidden mb-4 overflow-hidden rounded-xl border-2 border-indigo-600 bg-white shadow-lg">
            {/* 入力部分 */}
            <div onClick={handlePriceFocus} className="p-2">
              <NumericFormat
                value={bidPrice === 0 ? '' : bidPrice}
                thousandSeparator={true}
                placeholder="0"
                inputMode="none"
                readOnly={true}
                // 枠線を消して数字を強調
                className="w-full border-none p-2 text-right text-3xl font-black text-slate-800 focus:ring-0"
                isAllowed={(values) => values.value.length <= 7}
              />
            </div>
            
            {/* ボタン部分：入力欄と隙間なく結合 */}
            <button
              type="submit"
              disabled={isSaving || isBidDisabled}
              className="w-full bg-indigo-600 py-4 text-xl font-bold text-white transition-all active:bg-indigo-700 active:scale-[0.98] disabled:bg-slate-300"
            >
              <span className="tracking-[0.3em] ml-[0.3em]">
                {isSaving ? '送信中...' : '入札確定'}
              </span>
            </button>
          </div>

          {/* 【PC表示】*/}
          <div className="hidden md:block">
            <div onClick={handlePriceFocus} className="relative">
              <NumericFormat
                value={bidPrice === 0 ? '' : bidPrice}
                thousandSeparator={true}
                placeholder="0"
                inputMode={isMobile ? "none" : "numeric"}
                readOnly={isMobile}
                className={`${inputClasses} text-right font-bold text-2xl! pr-4`}
                isAllowed={(values) => values.value.length <= 7}
                onValueChange={(values) => {
                  if (!isMobile) setBidPrice(values.floatValue === undefined ? '' : values.floatValue);
                }}
              />
            </div>
            <p className="mt-1 w-full text-right text-sm font-bold text-slate-900">
              最低落札値: {item.bidMinPrice.toLocaleString()} 円
            </p>
          </div>
        </div>

        {/* 入札ボタン */}
        <div className="hidden md:flex md:col-start-3 items-end justify-end mb-2">
          <button 
            type="submit" 
            disabled={isSaving || isBidDisabled}
            className="w-48 rounded-md bg-indigo-600 py-2 text-base font-bold text-white shadow-sm transition-all hover:bg-indigo-700 active:scale-95 disabled:bg-slate-300"
          >
            {isSaving ? '送信中...' : '入　札　確　定'}
          </button>
        </div>
      </div>
      </form>
      

      {/* キーパッド */}
      <div className="lg:hidden flex-none bg-slate-200 border-t border-slate-200 pb-safe h-[320px]">
        <NumericKeypad 
          onNumClick={handleNumClick}
          onClear={handleClear}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};